import React, { useState, useRef, useLayoutEffect } from "react";
import ContentLoader from "react-content-loader";

export default function ImageWithSkeleton({
  src,
  alt,
  className = "",
  wrapperClassName = "",
  skeletonClassName = "",
  style = {},
  imgStyle = {},
  draggable = false,
  loading = "lazy",
  onLoad,
  onError,
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 400, height: 300 });
  const wrapRef = useRef(null);
  const imgRef = useRef(null);

  // Copy the computed border-radius from img to wrapper so the skeleton clips correctly
  useLayoutEffect(() => {
    if (wrapRef.current && imgRef.current) {
      const cs = window.getComputedStyle(imgRef.current);
      const br = cs.borderRadius;
      if (br) wrapRef.current.style.borderRadius = br;
      // Move margins from img to wrapper to keep layout position identical
      wrapRef.current.style.marginTop = cs.marginTop;
      wrapRef.current.style.marginRight = cs.marginRight;
      wrapRef.current.style.marginBottom = cs.marginBottom;
      wrapRef.current.style.marginLeft = cs.marginLeft;
      imgRef.current.style.margin = '0px';
      
      // Get actual dimensions for skeleton (only update if significantly different)
      const rect = imgRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const newWidth = Math.round(rect.width);
        const newHeight = Math.round(rect.height);
        setDimensions(prev => {
          // Only update if dimensions changed
          if (prev.width !== newWidth || prev.height !== newHeight) {
            return { width: newWidth, height: newHeight };
          }
          return prev;
        });
      }
    }
  }, []);

  const handleLoad = (e) => {
    setLoaded(true);
    onLoad && onLoad(e);
  };

  const handleError = (e) => {
    setErrored(true);
    onError && onError(e);
  };

  const passToWrapper = /wallet-card-logo|rectangle-image|park-image|product-image/.test(className || "");

  return (
    <div
      ref={wrapRef}
      className={`img-skeleton-wrap ${wrapperClassName} ${passToWrapper ? className : ""}`.trim()}
      style={{ ...style, position: 'relative', display: 'inline-block', overflow: 'hidden' }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {!loaded && !errored && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: 'inherit',
          }}
        >
          <ContentLoader
            speed={1.5}
            width={dimensions.width}
            height={dimensions.height}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            backgroundColor="#f0f0f0"
            foregroundColor="#e0e0e0"
            style={{ width: '100%', height: '100%' }}
          >
            <rect x="0" y="0" rx="0" ry="0" width={dimensions.width} height={dimensions.height} />
          </ContentLoader>
        </div>
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`img-skeleton-img ${className}`.trim()}
        onLoad={handleLoad}
        onError={handleError}
        draggable={draggable}
        loading={loading}
        style={{ display: 'block', borderRadius: 'inherit', ...imgStyle }}
        {...rest}
      />
    </div>
  );
}
